import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { type ReactNode, useState } from 'react';
import { type ConfigObject } from '../../_configs/types';
import { type Modules } from '../../modules/_definition/types';
import { generateLink } from '../../utils/generateLink';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import enhancedNavigate from '../../utils/tools/enhancedNavigate';
import Iconify from '../iconify/component';

type ErrorCardProps = {
    action?: 'reloadPage' | 'goHome';
    actionTooltip?: string;
    collapse?: {
        code: string;
        message: string;
        stack?: string;
    };
    description?: ReactNode;
    heading: ReactNode;
    level: 'error' | 'grey' | 'info' | 'primary';
    module: Modules;
    setErrorState?: (err: Error) => void;
    settings: ConfigObject;
};

function ErrorCard(props: ErrorCardProps) {
    const { action, actionTooltip, collapse, description, heading, level, module, setErrorState, settings } = props;
    const [collapseError, setCollapseError] = useState<boolean>(false);

    const theme = useTheme();
    const colors = {
        light: level === 'grey' ? theme?.palette?.[level]?.[300] : theme?.palette?.[level]?.light,
        main: level === 'grey' ? theme?.palette?.[level]?.[500] : theme?.palette?.[level]?.main,
        dark: level === 'grey' ? theme?.palette?.[level]?.[700] : theme?.palette?.[level]?.dark,
    };

    return (
        <Box sx={{ backgroundColor: alpha(colors.main, 0.05), borderRadius: 2, borderColor: colors.light, borderStyle: 'solid', borderWidth: 2, p: 2 }}>
            <Stack alignItems="center" direction="row" gap={2}>
                {level === 'error' ? (
                    <Iconify icon="mdi:alert-circle-outline" fontSize="large" sx={{ color: colors.main }} />
                ) : (
                    <Iconify icon="mdi:information-outline" fontSize="large" sx={{ color: colors.main }} />
                )}
                <Stack gap={1} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: colors.dark + '!important' }}>
                        {heading}
                    </Typography>
                    {!checkUndefinedNullOrEmpty(description) && (
                        <Typography variant="body2" sx={{ color: colors.dark + '!important' }}>
                            {description}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" flexWrap="nowrap">
                    {!checkUndefinedNullOrEmpty(action) && !checkUndefinedOrNull(setErrorState) && (
                        <Tooltip title={actionTooltip}>
                            <IconButton
                                onClick={function () {
                                    if (action === 'reloadPage') location.reload();
                                    // else if (action === 'exitError') this.setState({ error: null });
                                    else if (action === 'goHome') {
                                        setErrorState(null);
                                        enhancedNavigate(generateLink({ allFacets: undefined, filter: undefined, module, pathOnly: false, settings, type: 'filter' }));
                                    }
                                }}
                            >
                                {action === 'reloadPage' ? (
                                    <Iconify icon="mdi:refresh" color={level === 'grey' ? 'primary' : level} />
                                ) : /* action === 'exitError' ? <Iconify icon="mdi:close" /> : */ action === 'goHome' ? (
                                    <Iconify icon="mdi:home" color={level === 'grey' ? 'primary' : level} />
                                ) : null}
                            </IconButton>
                        </Tooltip>
                    )}
                    {!checkUndefinedNullOrEmpty(collapse) && (
                        <IconButton color={level === 'grey' ? 'default' : level} onClick={() => setCollapseError(!collapseError)}>
                            <Iconify icon="solar:alt-arrow-down-line-duotone" color={level === 'grey' ? 'primary' : level} sx={{ transform: collapseError ? 'rotate(180deg)' : undefined }} />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
            {!checkUndefinedNullOrEmpty(collapse) && (
                <Collapse in={collapseError}>
                    <Divider sx={{ backgroundColor: colors.light + '!important', mt: '16px!important', mb: '8px!important' }} />
                    <Stack gap={1}>
                        <Typography variant="caption" sx={{ color: colors.dark + '!important' }}>
                            {collapse.code}
                        </Typography>
                        <Typography variant="h6" sx={{ color: colors.dark + '!important' }}>
                            {collapse.message}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.dark + '!important' }}>
                            {collapse.stack}
                        </Typography>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}

export default ErrorCard;
